/**
 * The course frame's addressed views (#1393 M4). The frame mounts the chosen
 * row; a view brings its own Bento (headings from h2) and is never inside a
 * box (CASE-006). Desk is not a row: it is the bare course, with its Calendar
 * order and Resources part (A2). Each row's internals belong to its owning
 * area: A3 coursework, A4 people and grades, A7 all notices, A6 Manage course.
 */
import type { ReactNode } from "react";

import { CourseRecordingNotice } from "@/components/courses/CourseRecordingNotice";
import { CourseWorkspaceCoursework } from "@/components/courses/CourseWorkspaceCoursework";
import { GradebookGrid } from "@/components/gradebook/GradebookGrid";
import { CourseGroupsPart } from "@/components/courses/CourseGroupsPart";
import { GradesAttendancePart, TeachingAttendancePart } from "@/components/courses/GradesAttendancePart";
import { CourseTeachingPeopleFrame } from "@/components/courses/CoursePageFrames";
import type { CoursePart } from "@/components/courses/course-part";
import type { CourseHomeProjection } from "@/lib/api/courseHome";
import { AnnouncementsPage } from "@/pages/AnnouncementsPage";
import { GradesPage } from "@/pages/student/GradesPage";
import type { CourseWorkspaceView } from "@/navigation/course-workspace";

/** What the frame hands every view. */
export interface CourseViewProps {
  courseId: string;
  /** The course Home read, ready: the course, its term and the person's relationship. */
  projection: CourseHomeProjection;
  teaching: boolean;
  canEdit: boolean;
  /** Reads the course Home again (embedded views offer it on their own failure). */
  onRetryProjection(): void;
  /** The frame's own course-page boxes for this view: the learner's team, or Manage course's jobs. */
  frame?: ReactNode;
}

export type CourseViewOwner = "A3" | "A4" | "A6" | "A7";

export interface CourseViewRow {
  view: CourseWorkspaceView;
  owner: CourseViewOwner;
  /** The perspectives the view is offered to. */
  roles: ReadonlyArray<"teaching" | "learning">;
  /** The `?part=` sections this view draws for a perspective; an addressed one takes arrival focus from the frame. */
  parts?(props: { teaching: boolean; canEdit: boolean }): readonly CoursePart[];
  render(props: CourseViewProps): ReactNode;
}

const termOpen = (projection: CourseHomeProjection) => projection.course.term?.status !== "closed";

const BOTH = ["teaching", "learning"] as const;

export const COURSE_VIEWS: readonly CourseViewRow[] = [
  {
    view: "coursework",
    owner: "A3",
    roles: BOTH,
    render: ({ projection, canEdit, onRetryProjection }) =>
      <CourseWorkspaceCoursework projection={projection} onRetry={onRetryProjection} canEdit={canEdit} />,
  },
  {
    // People (#1393 contract 4): a teacher's team, private messages, roster
    // and roll, and course groups when they can edit; a learner's team and
    // contact only, with no peer roster or groups. A learner's own attendance
    // is in Grades (R1).
    view: "people",
    owner: "A4",
    roles: BOTH,
    parts: ({ teaching, canEdit }) => (teaching ? ["attendance", ...(canEdit ? ["groups" as const] : [])] : []),
    render: ({ courseId, projection, teaching, canEdit, frame }) =>
      teaching ? (
        <>
          <CourseTeachingPeopleFrame courseId={courseId} />
          <TeachingAttendancePart courseId={courseId} />
          {canEdit && <CourseGroupsPart key={courseId} courseId={courseId} writable={termOpen(projection)} timezone={projection.course.timezone} />}
        </>
      ) : (
        frame
      ),
  },
  {
    view: "grades",
    owner: "A4",
    roles: BOTH,
    parts: ({ teaching }) => (teaching ? [] : ["attendance"]),
    render: ({ courseId, teaching }) =>
      teaching ? <GradebookGrid key={courseId} courseId={courseId} mode="instructor" /> : (
        <>
          <GradesPage courseId={courseId} embedded />
          <GradesAttendancePart courseId={courseId} />
        </>
      ),
  },
  {
    view: "notices",
    owner: "A7",
    roles: BOTH,
    render: ({ courseId, teaching }) => (
      <>
        <AnnouncementsPage key={courseId} workspace={teaching ? "teaching" : "learning"} scope="course" courseId={courseId} embedded />
      </>
    ),
  },
  {
    // Manage course (#1393 contract 4): details, publication, connections and
    // the recording notice; an assigned administrator also changes the team.
    view: "settings",
    owner: "A6",
    roles: ["teaching"],
    render: ({ projection, frame }) => (
      <>
        {frame}
        <CourseRecordingNotice recordingPolicy={projection.recording_policy} />
      </>
    ),
  },
];

/** The row for a view this perspective is offered, or undefined. */
export function courseViewRow(view: string, teaching: boolean): CourseViewRow | undefined {
  return COURSE_VIEWS.find((row) => row.view === view && row.roles.includes(teaching ? "teaching" : "learning"));
}

